import { Modal } from '@mui/material';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Mail } from 'react-feather';
import { Trash, PlusCircle } from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import { AppDropDown } from '../../../general/AppDropDown/AppDropDown';
import { AppButton } from '../../../general/AppButton/AppButton';
import { AppRadioButton } from '../../../general/AppRadioButton/AppRadioButton';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import AppDatePicker from '../../../general/AppDatePicker/AppDatePicker';
import { DillCheckbox } from '../../../general/Dill/DillCheckbox';
import RadioInputs from '../CreditApplicationComponents/RadioInput';
import { UploadFileInputs } from '../CreditApplicationComponents/UploadFileInput';
import DocumentField from '../CreditApplicationComponents/DocumentField';
import InviteSignature from '../CreditApplicationComponents/InviteSignature';
import ConditionalOption from '../CreditApplicationComponents/ConditionalOption';
import AppBankReference from '../CreditApplicationComponents/AppBankReference';
import AppTradeReferenceUpload from '../CreditApplicationComponents/AppTradeReferenceUpload';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import SignatureDynamicFields from '../CreditApplicationComponents/SignatureDynamicFields';
import {
  ConditionalOptionValues,
  CreditApplicationStepField,
  DynamicInvitedSignaturesFields,
} from '@dill/dill-shared';
import ConditionalSelectedOptionFields, {
  ConditionalSelectedOptionFieldsRef,
} from '../CreditApplicationComponents/ConditionalSelectedOptionFields';
import ConditionalOptionPreview from '../CreditApplicationComponents/ConditionalOptionPreview';
import { collectIds, standardFieldIds } from './CONSTANTS';
import type { Step, StepField } from './CONSTANTS';
import StateDropDown from '../CreditApplicationComponents/StateDropDown';
import CheckboxList from '../CreditApplicationComponents/CheckboxList';
import OptionsDropDown from '../CreditApplicationComponents/OptionsDropDown';
import ConditionalForm from '../CreditApplicationComponents/ConditionalForm';
import FormDocumentFields from '../CreditApplicationComponents/FormDocumentFields';
import {
  resolveFieldOptions,
  resolveDropdownFieldOptions,
} from '../CreditApplicationComponents/conditionalOptionUtils';
import DynamicForm from '../CreditApplicationComponents/DynamicForm';
import { firebaseLargeFileUploadHelper, isHTML } from '../../../../utils/helpers';
import { useConditionalOption } from '../CreditApplicationComponents/useConditionalOption';

const PreviewConditionalSelectedField = ({
  creditApplicationOptionData,
  name,
  isRequired,
  isRow,
  optionList,
}: {
  creditApplicationOptionData: Record<string, CreditApplicationStepField | null> | null;
  name: string;
  isRequired?: boolean;
  isRow?: boolean;
  optionList?: string;
}) => {
  const [previewSelectedOption, setPreviewSelectedOption] = useState('');
  const [previewFieldValues, setPreviewFieldValues] = useState<ConditionalOptionValues[]>([]);

  const {
    selectedOption,
    optionStates,
    errors,
    handleSelectOption,
    handleChangeValue,
    handleAddGroup,
    handleRemoveGroup,
  } = useConditionalOption({
    creditApplicationOptionData,
    externalSelectedOption: previewSelectedOption,
    externalFieldValues: previewFieldValues,
    staticPreview: true,
    onChangeData: (data) => {
      setPreviewSelectedOption(data.value);
      setPreviewFieldValues(data.fieldValue);
    },
  });

  return (
    <ConditionalOptionPreview
      creditApplicationOptionData={creditApplicationOptionData}
      name={name}
      isRequired={isRequired}
      isRow={isRow}
      optionList={optionList}
      selectedOption={selectedOption}
      currentOptionState={selectedOption ? optionStates[selectedOption] ?? null : null}
      fieldErrors={errors}
      onSelectOption={handleSelectOption}
      onChangeValue={handleChangeValue}
      onAddGroup={handleAddGroup}
      onRemoveGroup={handleRemoveGroup}
    />
  );
};

type FieldError = {
  name?: string;
  id?: string;
};

type Field = {
  name: string;
  id: string;
  fromField:
    | 'TEXT'
    | 'NUMBER'
    | 'CURRENCY'
    | 'DATE'
    | 'TEXT_AREA'
    | 'CHECK_BOX'
    | 'FILE'
    | 'OPTION_FIELD'
    | 'DOCUMENT_FIELD'
    | 'INVITE_USER'
    | 'CONDITIONAL_OPTION'
    | 'BANK_REFERENCE'
    | 'TRADE_REFERENCE'
    | 'CONDITIONAL_SELECTED_FIELD'
    | 'STATE_DROPDOWN'
    | 'CHECKBOX_LIST'
    | 'CONDITIONAL_FORM'
    | 'DYNAMIC_FORM_DOCUMENT'
    | 'AUTOFILL_CHECKBOX'
    | 'INTERNAL_SIGNER'
    | 'DROPDOWN';
  isRequired: boolean;
  prefix: string;
  suffix: string;
  alignment?: 'LEFT' | 'RIGHT' | 'CENTER';
  documentText?: string;
  isHashed?: boolean;
  isFieldEmail?: boolean;
  isRow?: boolean;
  optionList?: string;
  optionDescriptions?: string;
  optionValueSource?: 'NAME' | 'ERP_ID';
  optionLabelSource?: 'NAME' | 'ERP_ID';
  houseRepsOnly?: boolean;
  houseRepFieldLabel?: string;
  nonHouseRepFieldLabel?: string;
  buttonType?: 'PRIMARY' | 'SECONDARY' | 'TERTIARY';
  fontSize?: string;
  fontWeight?: string;
  spaceHeight?: string;
  whoNeedsToSign?: string;
  fullNameNeeded?: boolean;
  driverLicenseNeeded?: boolean;
  optionType?: string;
  secondaryText?: string;
  status?: 'Pending' | 'Requested' | 'Submitted' | 'Rejected';
  emailDate?: string;
  sendEmail?: boolean;
  signatureGroupName?: string;
  placeHolder?: string;
  type?: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
  minimumCreditAmount?: number;
  invitedSignaturesFields?: DynamicInvitedSignaturesFields[];
  minimumNumberOfSignatures?: number;
  onlyShowPlaidOption?: boolean;
  formTemplate?: string | null;
  linkedFieldId?: string | null;
  showIfNotPlaid?: boolean;
  defaultValue?: string | null;
  populateFieldSignatureId?: string | null;
  autoPopulateFieldIds?: string | null;
  copyFromFieldIds?: string | null;
  copyToFieldIds?: string | null;
  signaturePageFieldIds?: string | null;
  conditionalVisibility?: { sourceFieldId: string; sourceOptionValues: string[] } | null;
  conditionalRequired?: { sourceFieldId: string; sourceOptionValues: string[] } | null;
  sampleFile?: { name?: string; url?: string; fileType?: string } | null;
  datePickerFormat?: 'FULL_DATE' | 'MONTH_YEAR';
  validationType?: 'NONE' | 'EIN' | 'SSN' | 'PHONE';
  emailValidationType?: 'SINGLE' | 'MULTIPLE';
  maxLength?: number;
  currencyDecimalPlaces?: number;
  borderId?: string | null;
  labelSubtext?: string | null;
};

type FieldDetails = {
  id: string;
  index: number;
  columnType: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
  stepType: 'GROUPED' | 'NOT_GROUPED';
  fields: Field[];
  groupedPosition: number;
};

const CREDIT_FIELD_TYPES = ['GROUPED', 'NOT_GROUPED'];
const COLUMN_TYPES = ['ONE_COLUMN', 'TWO_COLUMNS', 'THREE_COLUMNS'];
const FIELD_TYPE = [
  'TEXT',
  'NUMBER',
  'CURRENCY',
  'DATE',
  'TEXT_AREA',
  'CHECK_BOX',
  'FILE',
  'OPTION_FIELD',
  'DOCUMENT_FIELD',
  'INVITE_USER',
  'CONDITIONAL_OPTION',
  'BANK_REFERENCE',
  'CONDITIONAL_SELECTED_FIELD',
  'STATE_DROPDOWN',
  'CHECKBOX_LIST',
  'CONDITIONAL_FORM',
  'DYNAMIC_FORM_DOCUMENT',
  'AUTOFILL_CHECKBOX',
  'DROPDOWN',
];

const CreditApplicationFieldsModal = ({
  open,
  handleClose,
  onSubmit,
  selectedStep,
  creditApplicationStepField,
  selectedBankReferenceFieldsRequired,
  stepFieldIds,
}: {
  open: boolean;
  handleClose: () => void;
  onSubmit: (data: {
    columnType: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
    stepType: 'GROUPED' | 'NOT_GROUPED';
    groupedFields: FieldDetails[];
    unGroupedFields: Field[];
    groupName?: string;
    isFieldArray: boolean;
    moreButtonName?: string;
    sendEmail?: boolean;
    bankReferenceRequiredFields?: string[];
    minimunNumberOfGroups?: number;
    signatureFields?: DynamicInvitedSignaturesFields[];
  }) => void;
  selectedStep: string;
  creditApplicationStepField?: CreditApplicationStepField | null;
  selectedBankReferenceFieldsRequired?: string[];
  stepFieldIds: string[];
}) => {
  const conditionalRefsMap = useRef<Record<string, ConditionalSelectedOptionFieldsRef | null>>({});
  const dynamicFormRef = useRef<ConditionalSelectedOptionFieldsRef>(null);
  const { user } = useAppSelector((globalState) => globalState.auth);
  const currentSupplier = useAppSelector((globalState) => globalState.auth.currentSupplier);
  const dispatch = useAppDispatch();
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (globalState) => globalState.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);
  const [groupType, setGroupType] = useState<'GROUPED' | 'NOT_GROUPED'>('NOT_GROUPED');
  const [groupName, setGroupName] = useState('');
  const [fieldColumn, setFieldColumn] = useState<'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS'>(
    'ONE_COLUMN'
  );
  const [groupedFields, setGroupedFields] = useState<FieldDetails[]>([
    {
      id: `grouped-field-${selectedStep}-field-1`,
      index: 1,
      columnType: 'ONE_COLUMN',
      stepType: 'NOT_GROUPED',
      fields: [
        {
          name: '',
          id: '',
          fromField: 'TEXT',
          isRequired: false,
          prefix: '',
          suffix: '',
        },
      ],
      groupedPosition: 0,
    },
  ]);
  const [unGroupedFields, setUnGroupedFields] = useState<Field[]>([
    {
      name: '',
      id: '',
      fromField: 'TEXT',
      isRequired: false,
      prefix: '',
      suffix: '',
    },
  ]);
  const [isFieldArray, setIsFieldArray] = useState(false);
  const [moreButtonName, setMoreButtonName] = useState('');
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, FieldError>>({});
  const [sendEmail, setSendEmail] = useState(false);
  const [hasTradeReferenceUpload, setHasTradeReferenceUpload] = useState(false);
  const [bankReferenceRequiredFields, setBankReferenceRequiredFields] = useState<string[]>([
    'bankName',
    'accountNumber',
  ]);
  const [minimunNumberOfGroups, setMinimunNumberOfGroups] = useState(1);
  const [signatureFields, setSignatureFields] = useState<DynamicInvitedSignaturesFields[]>([]);
  const [errorSignatureFields, setErrorSignatureFields] = useState<{
    [key: string]: { name?: string; id?: string };
  }>({});

  const [selectedSignatureFields, setSelectedSignatureFields] = useState<
    DynamicInvitedSignaturesFields[] | null
  >(null);
  const [conditionalOptions, setConditionalOptions] = useState<Record<string, string>>({});
  const [conditionalOptionError, setConditionalOptionError] = useState('');
  const [dynamicFormFieldsError, setDynamicFormFieldsError] = useState('');
  const [conditionalOptionFields, setConditionalOptionFields] = useState<Record<
    string,
    Record<string, CreditApplicationStepField | null>
  > | null>(null);
  const [selectedConditionalOptionFields, setSelectedConditionalOptionFields] = useState<Record<
    string,
    Record<string, CreditApplicationStepField | null>
  > | null>(null);
  const [isIdAutoCompleteOpen, setIsIdAutoCompleteOpen] = useState('');
  const [condVisRawText, setCondVisRawText] = useState<Record<number, string>>({});
  const [condReqRawText, setCondReqRawText] = useState<Record<number, string>>({});
  const [sampleFiles, setSampleFiles] = useState<Record<string, File>>({});
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && creditApplicationStepField) {
      setGroupType(creditApplicationStepField.stepType);
      setGroupName(creditApplicationStepField.groupName ?? '');
      setFieldColumn(creditApplicationStepField.columnType);
      setGroupedFields(
        (creditApplicationStepField.groupedFields ?? []).map((g) => ({
          ...g,
          stepType: 'GROUPED' as const,
        }))
      );
      setUnGroupedFields(creditApplicationStepField.unGroupedFields ?? []);
      setIsFieldArray(creditApplicationStepField.isFieldArray);
      setMoreButtonName(creditApplicationStepField.moreButtonName ?? '');
      setSendEmail(creditApplicationStepField.sendEmail ?? false);
      setHasTradeReferenceUpload(creditApplicationStepField.hasTradeReferenceUpload ?? false);
      setBankReferenceRequiredFields(selectedBankReferenceFieldsRequired ?? []);
      setMinimunNumberOfGroups(creditApplicationStepField.minimunNumberOfGroups ?? 1);
      let invitedSignaturesFields: DynamicInvitedSignaturesFields[] = [];
      if (creditApplicationStepField.stepType === 'NOT_GROUPED') {
        const invitedSignatures = (creditApplicationStepField.unGroupedFields ?? []).find(
          (un) => un.fromField === 'INVITE_USER'
        );
        invitedSignaturesFields = invitedSignatures?.invitedSignaturesFields ?? [];
      } else {
        const flatMapGroup = (creditApplicationStepField.groupedFields ?? []).flatMap((group) =>
          group.fields.map((field) => ({
            ...field,
          }))
        );
        const invitedSignatures = flatMapGroup.find((un) => un.fromField === 'INVITE_USER');
        invitedSignaturesFields = invitedSignatures?.invitedSignaturesFields ?? [];
      }
      setSelectedSignatureFields(invitedSignaturesFields);
      const allFields =
        creditApplicationStepField.stepType === 'GROUPED'
          ? (creditApplicationStepField.groupedFields ?? []).flatMap((g) => g.fields)
          : creditApplicationStepField.unGroupedFields ?? [];

      const conditionalSelecteds = allFields.filter(
        (un) => un.fromField === 'CONDITIONAL_SELECTED_FIELD'
      );
      if (conditionalSelecteds.length > 0) {
        const newOptions: Record<string, string> = {};
        const newConditionalFields: Record<
          string,
          Record<string, CreditApplicationStepField | null>
        > = {};
        for (const cs of conditionalSelecteds) {
          newOptions[cs.id] = cs.optionList ?? '';
          if (cs.conditionalOptionFields) {
            newConditionalFields[cs.id] = cs.conditionalOptionFields as Record<
              string,
              CreditApplicationStepField | null
            >;
          }
        }
        setConditionalOptions(newOptions);
        if (Object.keys(newConditionalFields).length > 0) {
          setSelectedConditionalOptionFields((prev) => ({ ...prev, ...newConditionalFields }));
        }
      }

      const dynamicFormSelected = allFields.find((un) => un.fromField === 'DYNAMIC_FORM_DOCUMENT');
      if (dynamicFormSelected?.conditionalOptionFields) {
        setSelectedConditionalOptionFields((prev) => ({
          ...prev,
          [dynamicFormSelected.id]: dynamicFormSelected.conditionalOptionFields as Record<
            string,
            CreditApplicationStepField | null
          >,
        }));
      }
    }
  }, [open, creditApplicationStepField, selectedBankReferenceFieldsRequired]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsIdAutoCompleteOpen('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsIdAutoCompleteOpen]);

  const currentStepFieldIds = useMemo(() => {
    let ids: string[] = [];

    if (creditApplicationStepField) {
      const step: Step = {
        name: selectedStep,
        id: '',
        fields: [creditApplicationStepField] as StepField[],
        position: 0,
      };

      ids = collectIds([step]);
    }
    const filteredIds = stepFieldIds.filter((id) => !ids.includes(id));
    return filteredIds;
  }, [creditApplicationStepField, stepFieldIds]);

  const branches = useMemo(() => {
    if (user && currentSupplier && supplierBranches.length > 0) {
      return supplierBranches.map((b) => ({
        name: b?.branchName ?? '',
        erpId: b?.erpId ?? '',
      }));
    }
    return [];
  }, [user, supplierBranches]);

  const salesReps = useMemo(() => {
    if (user && currentSupplier && supplierBranches.length > 0) {
      const seen = new Set<string>();
      return supplierBranches
        .flatMap((b) => b?.salesRep ?? [])
        .filter((rep) => {
          const name = rep?.name ?? '';
          if (!name || seen.has(name)) return false;
          seen.add(name);
          return true;
        })
        .map((rep) => ({ name: rep?.name ?? '', erpId: rep?.erpId ?? '' }));
    }
    return [];
  }, [user, supplierBranches]);

  const resetState = () => {
    setFieldColumn('ONE_COLUMN');
    setGroupType('NOT_GROUPED');
    setGroupName('');
    setSelectedSignatureFields(null);
    setGroupedFields([
      {
        id: '',
        index: 1,
        columnType: 'ONE_COLUMN',
        stepType: 'NOT_GROUPED',
        fields: [
          {
            name: '',
            id: '',
            fromField: 'TEXT',
            isRequired: false,
            prefix: '',
            suffix: '',
          },
        ],
        groupedPosition: 0,
      },
    ]);
    setUnGroupedFields([
      {
        name: '',
        id: '',
        fromField: 'TEXT',
        isRequired: false,
        prefix: '',
        suffix: '',
      },
    ]);
    setIsFieldArray(false);
    setMoreButtonName('');
    setErrorFields([]);
    setFieldErrors({});
    setSendEmail(false);
    setBankReferenceRequiredFields(['bankName', 'accountNumber']);
    setMinimunNumberOfGroups(1);
    setSignatureFields([]);
    setErrorSignatureFields({});
    setSelectedConditionalOptionFields(null);
    setConditionalOptions({});
    setConditionalOptionError('');
    setDynamicFormFieldsError('');
    setConditionalOptionFields(null);
    setIsIdAutoCompleteOpen('');
    setCondVisRawText({});
    setSampleFiles({});
  };

  const handleCloseModal = () => {
    resetState();
    handleClose();
  };

  const validateInput = () => {
    const errors: string[] = [];
    const fieldErrs: { [key: string]: { name?: string; id?: string } } = {};

    if (!groupType) errors.push('groupType');
    if (groupType === 'GROUPED' && !groupName) errors.push('groupName');
    if (groupType === 'NOT_GROUPED' && !fieldColumn) errors.push('fieldColumn');

    const ensureFieldErrs = (key: string) => {
      if (!fieldErrs[key]) {
        fieldErrs[key] = {};
      }
      return fieldErrs[key];
    };

    if (groupType === 'GROUPED') {
      groupedFields.forEach((group) => {
        group.fields.forEach((f, i) => {
          const key = `${group.id}-${i}`;
          const fe = ensureFieldErrs(key);

          if (!f.name?.trim()) {
            fe.name = 'Field name is required';
          }
          if (!f.id?.trim()) {
            fe.id = 'Field id is required';
          } else if (currentStepFieldIds.includes(f.id)) {
            fe.id = 'Field id already exists';
          }
        });
      });
    } else {
      unGroupedFields.forEach((f, i) => {
        const key = `ungrouped-${i}`;
        const fe = ensureFieldErrs(key);

        if (!f.name?.trim()) {
          fe.name = 'Field name is required';
        }
        if (!f.id?.trim()) {
          fe.id = 'Field id is required';
        } else if (currentStepFieldIds.includes(f.id)) {
          fe.id = 'Field id already exists';
        }
      });
    }

    setErrorFields(errors);
    setFieldErrors(fieldErrs);

    const hasFieldErrors = Object.values(fieldErrs).some((fe) => fe.name || fe.id);

    return errors.length === 0 && !hasFieldErrors;
  };

  const validateSignatureFields = () => {
    const errors: { [key: string]: { name?: string; id?: string } } = {};

    for (let i = 0; i < signatureFields.length; i++) {
      const field = signatureFields[i];
      const key = `signature-${i}-${field.index}`;

      if (!field.name || field.name.trim() === '') {
        errors[key] = { name: 'Field name is required' };
      }
      if (!field.id || field.id.trim() === '') {
        errors[key] = { ...errors[key], id: 'Field id is required' };
      } else if (currentStepFieldIds.includes(field.id)) {
        errors[key] = { ...errors[key], id: 'Field id already exists' };
      }
    }

    setErrorSignatureFields(errors);
    return Object.keys(errors).length === 0;
  };

  const handleValidateConditionalSelectedField = () => {
    const allValid = Object.values(conditionalRefsMap.current).every(
      (ref) => ref === null || ref?.validate()
    );
    if (!allValid) {
      setConditionalOptionError('Conditional Errors');
    }
    return allValid;
  };

  const handleValidateDynamicFormField = () => {
    if (dynamicFormRef.current?.validate()) {
      return true;
    } else {
      setDynamicFormFieldsError('Dynamic Form Errors');
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateInput()) return;

    if (!validateSignatureFields()) return;

    const filterInviteUserFields = (fields: Field[]) => {
      return fields.map((field) => {
        if (field.fromField !== 'INVITE_USER') {
          const { signatureGroupName, placeHolder, type, minimumCreditAmount, ...filteredField } =
            field;
          return filteredField;
        } else {
          if (
            field.fromField === 'INVITE_USER' &&
            (field.type === 'AUTHORIZED_AGENT' ||
              field.type === 'OTHER' ||
              field.type === 'PERSONAL')
          ) {
            const { minimumCreditAmount, ...filteredField } = field;
            return {
              ...filteredField,
              ...(signatureFields.length > 0 && { invitedSignaturesFields: signatureFields }),
            };
          }
        }
        return {
          ...field,
          ...(signatureFields.length > 0 && { invitedSignaturesFields: signatureFields }),
        };
      });
    };

    const filteredGroupedFields =
      groupType === 'GROUPED'
        ? groupedFields.map((group) => ({
            ...group,
            fields: filterInviteUserFields(group.fields),
          }))
        : [];

    const filteredUnGroupedFields =
      groupType === 'NOT_GROUPED' ? filterInviteUserFields(unGroupedFields) : [];

    const checkForConditionalSelectedFields = () => {
      const groupedNestedFields: Field[] = filteredGroupedFields.flatMap((group) => group.fields);
      const allFields: Field[] = [...groupedNestedFields, ...filteredUnGroupedFields];
      const check = allFields.some((field) => field.fromField === 'CONDITIONAL_SELECTED_FIELD');
      if (check) {
        return handleValidateConditionalSelectedField();
      }
      return true;
    };

    const checkForDynamicFormFields = () => {
      const groupedNestedFields: Field[] = filteredGroupedFields.flatMap((group) => group.fields);
      const allFields: Field[] = [...groupedNestedFields, ...filteredUnGroupedFields];
      const check = allFields.some((field) => field.fromField === 'DYNAMIC_FORM_DOCUMENT');

      if (check) {
        return handleValidateDynamicFormField();
      }
      return true;
    };

    if (!checkForConditionalSelectedFields()) return;

    if (!checkForDynamicFormFields()) return;

    // Upload pending sample files for direct fields
    const sampleFileUrlMap: Record<string, string> = {};
    for (const [fk, file] of Object.entries(sampleFiles)) {
      const url = await firebaseLargeFileUploadHelper({
        blobUrl: URL.createObjectURL(file),
        fileNameWithExtension: file.name,
        storagePath: `creditApplicationFiles/sampleFiles/${
          currentSupplier?.id || 'unknown'
        }/${Date.now()}_${file.name}`,
      });
      if (url) sampleFileUrlMap[fk] = url;
    }

    const injectSampleUrl = (field: Field, fieldKey: string): Field =>
      sampleFileUrlMap[fieldKey]
        ? { ...field, sampleFile: { ...field.sampleFile, url: sampleFileUrlMap[fieldKey] } }
        : field;

    const groupedFieldsWithUrls = filteredGroupedFields.map((group) => ({
      ...group,
      fields: group.fields.map((field, fIdx) => injectSampleUrl(field, `${group.id}-${fIdx}`)),
    }));

    const ungroupedFieldsWithUrls = filteredUnGroupedFields.map((field, idx) =>
      injectSampleUrl(field, `ungrouped-${idx}`)
    );

    // Get conditional field data with any uploaded sample files
    let effectiveConditionalOptionFields = conditionalOptionFields;
    for (const ref of Object.values(conditionalRefsMap.current)) {
      if (ref) {
        const uploadedConditional = await ref.uploadSampleFiles();
        if (uploadedConditional) {
          effectiveConditionalOptionFields = {
            ...effectiveConditionalOptionFields,
            ...uploadedConditional,
          };
        }
      }
    }

    const addConditionalSelectedField = (fields: Field[]) => {
      return fields.map((field) => {
        if (
          field.fromField === 'CONDITIONAL_SELECTED_FIELD' &&
          effectiveConditionalOptionFields &&
          effectiveConditionalOptionFields[field.id]
        ) {
          return {
            ...field,
            conditionalOptionFields: effectiveConditionalOptionFields[field.id],
          };
        } else if (
          field.fromField === 'DYNAMIC_FORM_DOCUMENT' &&
          effectiveConditionalOptionFields &&
          effectiveConditionalOptionFields[field.id]
        ) {
          return {
            ...field,
            conditionalOptionFields: effectiveConditionalOptionFields[field.id],
          };
        } else {
          const { conditionalOptionFields: ignoredConditionalOptionFields, ...filteredField } =
            field as Field & { conditionalOptionFields?: unknown };
          return filteredField;
        }
      });
    };

    const addedConditionalSelectedFieldsGrouped =
      groupType === 'GROUPED'
        ? groupedFieldsWithUrls.map((group) => ({
            ...group,
            fields: addConditionalSelectedField(group.fields),
          }))
        : [];

    const addedConditionalSelectedFieldsUnGrouped =
      groupType === 'NOT_GROUPED' ? addConditionalSelectedField(ungroupedFieldsWithUrls) : [];

    const data = {
      columnType: fieldColumn,
      stepType: groupType,
      groupedFields: addedConditionalSelectedFieldsGrouped,
      unGroupedFields: addedConditionalSelectedFieldsUnGrouped,
      groupName,
      isFieldArray,
      moreButtonName: isFieldArray ? moreButtonName || groupName : groupName,
      ...(sendEmail && { sendEmail }),
      ...(groupType === 'GROUPED' && hasTradeReferenceUpload && { hasTradeReferenceUpload }),
      ...(bankReferenceRequiredFields.length > 0 &&
        filteredUnGroupedFields.filter((f) => f.fromField === 'BANK_REFERENCE').length > 0 && {
          bankReferenceRequiredFields,
        }),
      ...(groupType === 'GROUPED' && { minimunNumberOfGroups }),
    };

    onSubmit(data);
    handleCloseModal();
  };

  const handleChangeConditionalField = (
    data: Record<string, Record<string, CreditApplicationStepField | null>> | null
  ) => {
    setConditionalOptionError('');
    setConditionalOptionFields((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const handleChangeFormDocumentField = (
    data: Record<string, Record<string, CreditApplicationStepField | null>> | null
  ) => {
    setDynamicFormFieldsError('');
    setConditionalOptionFields((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const filteredIdSuggestionsOptions = (text: string) => {
    const filteredStardardFieldIds = standardFieldIds.filter(
      (id) => !currentStepFieldIds.includes(id)
    );

    return filteredStardardFieldIds.filter((option) =>
      option.toLowerCase().includes(text.trim().toLowerCase())
    );
  };

  const getColumnsCount = (type: string) =>
    type === 'ONE_COLUMN' ? 1 : type === 'TWO_COLUMNS' ? 2 : 3;

  const getGridColsClass = (count: number) =>
    count === 1 ? 'grid-cols-1' : count === 2 ? 'grid-cols-2' : 'grid-cols-3';

  const renderFieldInputs = (
    fields: Field[],
    groupIndex: number,
    onChange: <K extends keyof Field>(fieldIndex: number, key: K, value: Field[K]) => void
  ) => {
    return fields.map((field, idx) => {
      const fieldKey =
        groupType === 'GROUPED' ? `${groupedFields[groupIndex]?.id}-${idx}` : `ungrouped-${idx}`;
      const idSuggestedOptions = filteredIdSuggestionsOptions(field.id);
      return (
        <div key={idx} className="min-w-0 rounded GREY_300-BORDER border p-2">
          <div className="w-full mt-2 mb-4">
            <AppInputField
              isRequired
              placeholder="Enter field name"
              label="Field Name"
              value={field.name}
              errorText={fieldErrors[fieldKey]?.name || ''}
              onBlur={() => {
                if (field.name?.trim() !== field.name) {
                  onChange(idx, 'name', field.name.trim());
                }
              }}
              onTextChange={(text) => {
                onChange(idx, 'name', text);
                setFieldErrors((prev) => ({
                  ...prev,
                  [fieldKey]: {
                    ...prev[fieldKey],
                    name: '',
                  },
                }));
              }}
            />
          </div>
          <div className="w-full mt-2 mb-4 relative" ref={wrapperRef}>
            <AppInputField
              isRequired
              placeholder="Enter field id"
              label="Field Id"
              value={field.id}
              onFocus={() => setIsIdAutoCompleteOpen(`focus-id-${fieldKey}`)}
              onMouseUp={() => setIsIdAutoCompleteOpen(`focus-id-${fieldKey}`)}
              errorText={fieldErrors[fieldKey]?.id || ''}
              onBlur={() => {
                if (field.id?.trim() !== field.id) {
                  onChange(idx, 'id', field.id.trim());
                }
              }}
              onTextChange={(text) => {
                onChange(idx, 'id', text);
                setFieldErrors((prev) => ({
                  ...prev,
                  [fieldKey]: {
                    ...prev[fieldKey],
                    id: '',
                  },
                }));
              }}
            />
            {isIdAutoCompleteOpen === `focus-id-${fieldKey}` && idSuggestedOptions.length > 0 && (
              <div className="absolute w-full mt-1 bg-white border rounded-lg shadow-md z-50">
                <div className="p-2 max-h-60 overflow-y-auto">
                  {idSuggestedOptions.map((option, i) => (
                    <div
                      key={`${option}_${i}-id-option`}
                      onMouseDown={() => {
                        onChange(idx, 'id', option);
                        setIsIdAutoCompleteOpen('');
                      }}
                      className="p-1 hover:bg-gray-100 cursor-pointer text-xs">
                      {option}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="w-full my-2">
            <AppDropDown
              label="Field Type"
              isRequired
              value={field.fromField}
              items={FIELD_TYPE.map((type) => ({ label: type.replaceAll('_', ' '), value: type }))}
              onSelectChange={(item) => {
                if (item) {
                  onChange(idx, 'fromField', item.value);
                  onChange(idx, 'isRequired', false);
                  onChange(idx, 'isHashed', false);
                  if (field.fromField === 'INVITE_USER') {
                    onChange(idx, 'minimumCreditAmount', 0);
                    onChange(idx, 'type', 'AUTHORIZED_AGENT');
                  }
                }
              }}
            />
          </div>
          {field.fromField === 'DATE' && (
            <div className="my-2">
              <AppDropDown
                label="Date Format"
                value={field.datePickerFormat ?? 'FULL_DATE'}
                items={[
                  { label: 'Full Date (MM/DD/YYYY)', value: 'FULL_DATE' },
                  { label: 'Month & Year (MM/YYYY)', value: 'MONTH_YEAR' },
                ]}
                onSelectChange={(item) => {
                  if (item)
                    onChange(idx, 'datePickerFormat', item.value as 'FULL_DATE' | 'MONTH_YEAR');
                }}
              />
            </div>
          )}
          {field.fromField === 'DATE' && (
            <div className="my-2 flex flex-col">
              <AppInputField
                label="Default Value"
                value={field.defaultValue != null ? field?.defaultValue : ''}
                onTextChange={(text) => {
                  onChange(idx, 'defaultValue', text);
                }}
              />
            </div>
          )}
          <div className="my-2 flex flex-col">
            <AppInputField
              label="Linked Field Id"
              value={field.linkedFieldId !== undefined ? field?.linkedFieldId : ''}
              onTextChange={(text) => {
                onChange(idx, 'linkedFieldId', text);
              }}
            />
          </div>
          <div className="my-2 flex flex-col">
            <AppInputField
              inputType="textarea"
              label="Label Subtext"
              placeholder="Enter subtext"
              value={field.labelSubtext != null ? field.labelSubtext : ''}
              onTextChange={(text) => {
                onChange(idx, 'labelSubtext', text || null);
              }}
            />
            <p className="text-xs ml-1 text-left PRIMARY_300-CLR">
              Optional text shown below the label. Plain text or HTML.
            </p>
          </div>
          {field.fromField === 'DYNAMIC_FORM_DOCUMENT' && (
            <div className="my-2 flex flex-col">
              <AppInputField
                label="Signature Field that pupulates data"
                value={
                  field.populateFieldSignatureId !== undefined
                    ? field?.populateFieldSignatureId
                    : ''
                }
                onTextChange={(text) => {
                  onChange(idx, 'populateFieldSignatureId', text);
                }}
              />
            </div>
          )}
          {field.fromField === 'DYNAMIC_FORM_DOCUMENT' && (
            <div className="my-2 flex flex-col">
              <AppInputField
                label="Auto-populate Field IDs"
                placeholder="e.g. field-id-1, field-id-2"
                value={field.autoPopulateFieldIds != null ? field.autoPopulateFieldIds : ''}
                onTextChange={(text) => {
                  onChange(idx, 'autoPopulateFieldIds', text);
                }}
              />
              <p className="text-xs ml-1 text-left PRIMARY_300-CLR">
                Comma-separated field IDs whose values will auto-populate in the document
              </p>
            </div>
          )}
          {field.fromField === 'AUTOFILL_CHECKBOX' && (
            <div className="my-2 flex flex-col">
              <AppInputField
                label="Copy From Field Id"
                value={field.copyFromFieldIds != null ? field.copyFromFieldIds : ''}
                onTextChange={(text) => {
                  onChange(idx, 'copyFromFieldIds', text);
                }}
              />
              <p className="text-xs ml-1 text-left PRIMARY_300-CLR">
                Field id whose value will be copied when checked
              </p>
            </div>
          )}
          {field.fromField === 'AUTOFILL_CHECKBOX' && (
            <div className="my-2 flex flex-col">
              <AppInputField
                label="Copy To Field Ids"
                value={field.copyToFieldIds != null ? field.copyToFieldIds : ''}
                onTextChange={(text) => {
                  onChange(idx, 'copyToFieldIds', text);
                }}
              />
              <p className="text-xs ml-1 text-left PRIMARY_300-CLR">
                Comma-separated field ids that will receive the copied value
              </p>
            </div>
          )}
          {(field.fromField === 'CONDITIONAL_OPTION' || field.fromField === 'CONDITIONAL_FORM') && (
            <div className="w-full my-2">
              <AppDropDown
                label="Option Type"
                isRequired
                value={field.optionType ?? ''}
                items={[
                  { label: 'Text', value: 'text' },
                  { label: 'File', value: 'file' },
                ]}
                onSelectChange={(item) => {
                  if (item) {
                    onChange(idx, 'optionType', item.value);
                  }
                }}
              />
            </div>
          )}
          {(field.fromField === 'TEXT' ||
            field.fromField === 'NUMBER' ||
            field.fromField === 'CURRENCY') && (
            <>
              <div className="my-2">
                <AppInputField
                  label="Prefix"
                  value={field.prefix}
                  onTextChange={(text) => {
                    onChange(idx, 'prefix', text);
                  }}
                />
              </div>
              <div className="my-2">
                <AppInputField
                  label="Suffix"
                  value={field.suffix}
                  onTextChange={(text) => {
                    onChange(idx, 'suffix', text);
                  }}
                />
              </div>
            </>
          )}
          {field.fromField === 'CURRENCY' && (
            <div className="my-2">
              <AppDropDown
                label="Decimal Places"
                value={String(
                  field.currencyDecimalPlaces !== undefined ? field.currencyDecimalPlaces : 2
                )}
                items={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
                  label: String(n),
                  value: String(n),
                }))}
                onSelectChange={(item) => {
                  if (item) onChange(idx, 'currencyDecimalPlaces', parseInt(item.value, 10));
                }}
              />
            </div>
          )}
          {(field.fromField === 'CONDITIONAL_OPTION' || field.fromField === 'CONDITIONAL_FORM') && (
            <div className="my-2 flex flex-col">
              <AppInputField
                label="Secondary Text"
                value={field.secondaryText || ''}
                onTextChange={(text) => {
                  onChange(idx, 'secondaryText', text);
                }}
              />
            </div>
          )}
          {(field.fromField === 'OPTION_FIELD' ||
            (field.fromField === 'CHECKBOX_LIST' &&
              !field.id.includes('supplier-branches') &&
              !field.id.includes('supplier-sales-reps'))) && (
            <div className="my-2 flex flex-col">
              <AppInputField
                label="Option List"
                value={field.optionList || ''}
                onTextChange={(text) => {
                  onChange(idx, 'optionList', text);
                }}
              />
              <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                Add option list separed by comma
              </p>
            </div>
          )}
          {field.fromField === 'DROPDOWN' &&
            !field.id.includes('supplier-branches') &&
            !field.id.includes('supplier-sales-reps') && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  label="Options"
                  value={field.optionList || ''}
                  onTextChange={(text) => {
                    onChange(idx, 'optionList', text);
                  }}
                />
                <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                  Add option list separated by &quot;::&quot;
                </p>
              </div>
            )}
          {(field.id.includes('supplier-branches') || field.id.includes('supplier-sales-reps')) &&
            (field.fromField === 'OPTION_FIELD' ||
              field.fromField === 'CHECKBOX_LIST' ||
              field.fromField === 'DROPDOWN') && (
              <div className="w-full my-2 flex flex-col gap-2">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <AppDropDown
                      label="Value to save"
                      value={field.optionValueSource ?? 'NAME'}
                      items={[
                        { label: 'Name', value: 'NAME' },
                        { label: 'ERP ID', value: 'ERP_ID' },
                      ]}
                      onSelectChange={(item) => {
                        if (item) {
                          onChange(idx, 'optionValueSource', item.value as 'NAME' | 'ERP_ID');
                        }
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <AppDropDown
                      label="Label to show"
                      value={field.optionLabelSource ?? 'NAME'}
                      items={[
                        { label: 'Name', value: 'NAME' },
                        { label: 'ERP ID', value: 'ERP_ID' },
                      ]}
                      onSelectChange={(item) => {
                        if (item) {
                          onChange(idx, 'optionLabelSource', item.value as 'NAME' | 'ERP_ID');
                        }
                      }}
                    />
                  </div>
                </div>
                <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                  ERP ID avoids ambiguity if two branches/reps share a name. Value and label are
                  independent — e.g. save the ERP ID while still showing the name.
                </p>
              </div>
            )}
          {field.id.includes('supplier-sales-reps') &&
            (field.fromField === 'OPTION_FIELD' ||
              field.fromField === 'CHECKBOX_LIST' ||
              field.fromField === 'DROPDOWN') && (
              <div className="w-full my-2 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <DillCheckbox
                    isChecked={field.houseRepsOnly ?? false}
                    onChange={(checked: boolean) => onChange(idx, 'houseRepsOnly', checked)}
                  />
                  <span
                    className="text-sm cursor-pointer"
                    onClick={() => onChange(idx, 'houseRepsOnly', !field.houseRepsOnly)}>
                    Only show House Reps
                  </span>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <AppInputField
                      label="Field label when a House Rep is selected"
                      value={field.houseRepFieldLabel || ''}
                      placeholder="e.g. House Account Id"
                      onTextChange={(text) => onChange(idx, 'houseRepFieldLabel', text)}
                    />
                  </div>
                  <div className="flex-1">
                    <AppInputField
                      label="Field label otherwise"
                      value={field.nonHouseRepFieldLabel || ''}
                      placeholder="e.g. Sales Rep Id"
                      onTextChange={(text) => onChange(idx, 'nonHouseRepFieldLabel', text)}
                    />
                  </div>
                </div>
                <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                  Leave both blank to keep the Field Name above fixed regardless of which rep is
                  selected.
                </p>
              </div>
            )}
          {field.fromField === 'OPTION_FIELD' && (
            <div className="my-2 flex flex-col">
              <AppInputField
                label="Option Descriptions"
                value={field.optionDescriptions || ''}
                onTextChange={(text) => {
                  onChange(idx, 'optionDescriptions', text);
                }}
              />
              <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                Optional. Add one description per option, separated by &quot;;;&quot;, in the same
                order as the Option List above.
              </p>
            </div>
          )}
          {field.fromField === 'INVITE_USER' && (
            <div className="my-2 flex items-center">
              <AppDropDown
                label="Type of signer"
                isRequired
                value={field?.type ?? ''}
                errorText={errorFields.includes('groupType') ? 'Please select' : ''}
                items={[
                  'AUTHORIZED_AGENT',
                  'GUARANTOR',
                  'OTHER',
                  'PERSONAL',
                  'CORPORATE_GUARANTOR',
                ].map((type) => ({
                  label: type.replaceAll('_', ' '),
                  value: type,
                }))}
                onSelectChange={(item) => {
                  if (item) {
                    onChange(
                      idx,
                      'type',
                      item.value as
                        | 'AUTHORIZED_AGENT'
                        | 'GUARANTOR'
                        | 'OTHER'
                        | 'PERSONAL'
                        | 'CORPORATE_GUARANTOR'
                    );
                  }
                }}
              />
            </div>
          )}
          {field.fromField === 'INVITE_USER' && (
            <div className="my-2 flex flex-col">
              <AppInputField
                isRequired
                label="Who needs to sign"
                value={field.whoNeedsToSign || ''}
                onTextChange={(text) => {
                  onChange(idx, 'whoNeedsToSign', text);
                }}
              />
              <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                Add titles of who needs to sign separed by comma
              </p>
            </div>
          )}

          {field.fromField === 'INVITE_USER' && (
            <div className="my-2 flex flex-col">
              <AppInputField
                inputType="number"
                label="Minimum Number of Signatures"
                value={
                  field.minimumNumberOfSignatures !== undefined
                    ? field.minimumNumberOfSignatures
                    : ''
                }
                onTextChange={(text) => {
                  onChange(
                    idx,
                    'minimumNumberOfSignatures',
                    text === '' ? undefined : Number(text)
                  );
                }}
              />
            </div>
          )}
          {field.fromField !== 'DOCUMENT_FIELD' &&
            field.fromField !== 'INVITE_USER' &&
            field.fromField !== 'DYNAMIC_FORM_DOCUMENT' && (
              <div className="my-2 flex items-center">
                <AppRadioButton
                  isChecked={field.isRequired}
                  onClick={(val: boolean) => {
                    onChange(idx, 'isRequired', val);
                  }}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() => {
                    onChange(idx, 'isRequired', !field.isRequired);
                  }}>
                  Is Field Required
                </p>
              </div>
            )}
          {field.fromField === 'BANK_REFERENCE' && (
            <div className="my-2 flex items-center">
              <AppRadioButton
                isChecked={field.onlyShowPlaidOption}
                onClick={(val: boolean) => {
                  onChange(idx, 'onlyShowPlaidOption', val);
                }}
              />
              <p
                className="text-sm ml-1 cursor-pointer"
                onClick={() => {
                  onChange(idx, 'onlyShowPlaidOption', !field.onlyShowPlaidOption);
                }}>
                Show only plaid option
              </p>
            </div>
          )}
          {field.fromField === 'BANK_REFERENCE' && (
            <div className="my-2 flex items-center">
              <AppRadioButton
                isChecked={field.showIfNotPlaid}
                onClick={(val: boolean) => {
                  onChange(idx, 'showIfNotPlaid', val);
                }}
              />
              <p
                className="text-sm ml-1 cursor-pointer"
                onClick={() => {
                  onChange(idx, 'showIfNotPlaid', !field.showIfNotPlaid);
                }}>
                Show field if not plaid
              </p>
            </div>
          )}
          {field.fromField === 'BANK_REFERENCE' && (
            <div className="my-2 items-center text-xs">
              <div>
                Apart from Bank and Name and Account Number what other field will be required for
                the user to fill manually
              </div>
              <div className="flex gap-4 items-center ">
                {[
                  { name: 'Address', value: 'address' },
                  { name: 'Phone Number', value: 'phoneNumber' },
                  { name: 'Contact Person', value: 'contactPerson' },
                ].map((option) => (
                  <div className="flex my-1 items-center" key={`item-${option.value}`}>
                    <AppRadioButton
                      isChecked={[...bankReferenceRequiredFields].includes(option.value)}
                      onClick={(val: boolean) => {
                        if (val) {
                          setBankReferenceRequiredFields((prev) => [...prev, option.value]);
                        } else {
                          setBankReferenceRequiredFields((prev) =>
                            prev.filter((bankReferenceField) => bankReferenceField !== option.value)
                          );
                        }
                      }}
                    />
                    <p
                      className="text-xs ml-1 cursor-pointer"
                      onClick={() => {
                        if (!bankReferenceRequiredFields.includes(option.value)) {
                          setBankReferenceRequiredFields((prev) => [...prev, option.value]);
                        } else {
                          setBankReferenceRequiredFields((prev) =>
                            prev.filter((bankReferenceField) => bankReferenceField !== option.value)
                          );
                        }
                      }}>
                      {option.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {field.fromField === 'TEXT' && (
            <div className="my-2 flex items-center">
              <AppRadioButton
                isChecked={field.isFieldEmail || false}
                onClick={(val: boolean) => {
                  onChange(idx, 'isFieldEmail', val);
                  if (!val) onChange(idx, 'emailValidationType', undefined);
                }}
              />
              <p
                className="text-sm ml-1 cursor-pointer"
                onClick={() => {
                  const next = !field.isFieldEmail;
                  onChange(idx, 'isFieldEmail', next);
                  if (!next) onChange(idx, 'emailValidationType', undefined);
                }}>
                Is field an email
              </p>
            </div>
          )}
          {field.fromField === 'TEXT' && field.isFieldEmail && (
            <div className="my-2">
              <AppDropDown
                label="Email Validation Type"
                value={field.emailValidationType ?? 'SINGLE'}
                items={[
                  { label: 'Single email', value: 'SINGLE' },
                  { label: 'Multiple emails (comma or semicolon separated)', value: 'MULTIPLE' },
                ]}
                onSelectChange={(item) => {
                  if (item)
                    onChange(idx, 'emailValidationType', item.value as 'SINGLE' | 'MULTIPLE');
                }}
              />
            </div>
          )}
          {field.fromField === 'TEXT' && (
            <div className="my-2">
              <AppDropDown
                label="Validation Type"
                value={field.validationType ?? 'NONE'}
                items={[
                  { label: 'None', value: 'NONE' },
                  { label: 'Tax ID / EIN (XX-XXXXXXX)', value: 'EIN' },
                  { label: 'SSN (XXX-XX-XXXX)', value: 'SSN' },
                  { label: 'Phone Number (XXX-XXX-XXXX)', value: 'PHONE' },
                ]}
                onSelectChange={(item) => {
                  if (item)
                    onChange(idx, 'validationType', item.value as 'NONE' | 'EIN' | 'SSN' | 'PHONE');
                }}
              />
            </div>
          )}
          {(field.fromField === 'TEXT' || field.fromField === 'TEXT_AREA') && (
            <div className="my-2">
              <AppInputField
                label="Max Length"
                inputType="number"
                placeholder="No limit"
                value={field.maxLength !== undefined ? field.maxLength : ''}
                onTextChange={(text) => {
                  onChange(idx, 'maxLength', text === '' ? undefined : Number(text));
                }}
              />
              <p className="text-xs ml-1 text-left PRIMARY_300-CLR">
                Maximum number of characters allowed
              </p>
            </div>
          )}
          {(field.fromField === 'TEXT' || field.fromField === 'NUMBER') && (
            <div className="my-2 flex items-center">
              <AppRadioButton
                isChecked={field.isHashed || false}
                onClick={(val: boolean) => {
                  onChange(idx, 'isHashed', val);
                }}
              />
              <p
                className="text-sm ml-1 cursor-pointer"
                onClick={() => {
                  onChange(idx, 'isHashed', !field.isHashed);
                }}>
                Is Field Hashed
              </p>
            </div>
          )}
          {field.fromField === 'FILE' && (
            <div className="my-2 border-t border-gray-100 pt-2">
              <p className="text-xs font-medium text-gray-500 mb-1">Sample File</p>
              <UploadFileInputs
                label="Upload sample file"
                onSelectFile={({ file }) => {
                  setSampleFiles((prev) => ({ ...prev, [fieldKey]: file }));
                  onChange(idx, 'sampleFile', {
                    name: file.name,
                    fileType: file.name.split('.').pop() || '',
                    url: field.sampleFile?.url,
                  });
                }}
                selectedFileName={sampleFiles[fieldKey]?.name || field.sampleFile?.name}
              />
              {field.sampleFile?.url && !sampleFiles[fieldKey] && (
                <a
                  href={field.sampleFile.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs PRIMARY_300-CLR underline mt-1 inline-block">
                  Preview / Download sample
                </a>
              )}
            </div>
          )}
          {field.fromField === 'INVITE_USER' &&
            (field?.type === 'GUARANTOR' || field?.type === 'CORPORATE_GUARANTOR') && (
              <div className="my-2 flex items-center">
                <AppInputField
                  label="Minimum Credit Amount"
                  value={field?.minimumCreditAmount?.toString() ?? ''}
                  onTextChange={(text) => onChange(idx, 'minimumCreditAmount', Number(text))}
                  inputType="number"
                  isRequired
                  placeholder="Enter minimum credit amount"
                  onBlur={() => {
                    if (field?.minimumCreditAmount && field?.minimumCreditAmount < 1) {
                      onChange(idx, 'minimumCreditAmount', 1);
                    }
                  }}
                  errorText={
                    errorFields.includes('minimumCreditAmount')
                      ? 'Minimum credit amount is required'
                      : ''
                  }
                />
              </div>
            )}
          {field.fromField === 'INVITE_USER' && (
            <div className="my-2 flex flex-col">
              <AppInputField
                label="Signature Page Field IDs"
                placeholder="e.g. company-name, contact-email"
                value={field.signaturePageFieldIds != null ? field.signaturePageFieldIds : ''}
                onTextChange={(text) => {
                  onChange(idx, 'signaturePageFieldIds', text || null);
                }}
              />
              <p className="text-xs ml-1 text-left PRIMARY_300-CLR">
                Comma-separated field IDs to show on the signature page. Leave empty to show all
                fields.
              </p>
            </div>
          )}
          {field.fromField === 'INVITE_USER' && (
            <div className="my-2 flex items-center">
              <SignatureDynamicFields
                onSubmit={(data: { fields: DynamicInvitedSignaturesFields[] }) => {
                  setSignatureFields(data.fields);
                }}
                errorSignatureFields={errorSignatureFields}
                valueData={signatureFields}
                selectedSignatureFields={selectedSignatureFields}
                filteredStardardFieldIds={standardFieldIds.filter(
                  (id) => !currentStepFieldIds.includes(id)
                )}
              />
            </div>
          )}
          {field.fromField === 'DOCUMENT_FIELD' && (
            <div className="my-2 flex items-center flex-col">
              <AppInputField
                inputType="textarea"
                label="Document Text"
                value={field.documentText || ''}
                onTextChange={(text) => {
                  onChange(idx, 'documentText', text);
                }}
              />
            </div>
          )}
          {field.fromField === 'DOCUMENT_FIELD' && (
            <div className="my-2 flex items-center flex-col">
              <AppInputField
                label="Font Size"
                value={field.fontSize || ''}
                onTextChange={(text) => {
                  onChange(idx, 'fontSize', text);
                }}
              />
            </div>
          )}
          {field.fromField === 'DOCUMENT_FIELD' && (
            <div className="my-2 flex items-center flex-col justify-start">
              <p>Font Weight</p>
              <RadioInputs
                id={`fontWeight-${idx}`}
                selectedOption={field.fontWeight || 'font-normal'}
                options={['font-thin', 'font-normal', 'font-semibold', 'font-bold']}
                onChange={({ name, value }) => {
                  onChange(idx, 'fontWeight', value);
                }}
                className="gap-2 flex items-center"
              />
            </div>
          )}
          {(field.fromField === 'DOCUMENT_FIELD' || field.fromField === 'CHECK_BOX') && (
            <div className="my-2 flex items-center flex-col justify-start">
              <p>How are the fields aligned in the document? (LEFT, RIGHT, CENTER)</p>
              <RadioInputs
                id={`alignment-${idx}`}
                selectedOption={field.alignment || 'LEFT'}
                options={['LEFT', 'CENTER', 'RIGHT']}
                onChange={({ name, value }) => {
                  onChange(idx, 'alignment', value as 'LEFT' | 'RIGHT' | 'CENTER');
                }}
                className="gap-2 flex items-center"
              />
            </div>
          )}
          <div className="my-2 flex items-center flex-col">
            <AppInputField
              label="Border Group: Last Field Id (optional)"
              value={field.borderId || ''}
              onTextChange={(text) => {
                onChange(idx, 'borderId', text || null);
              }}
            />
            <p className="text-xs text-gray-400 mt-1">
              Set this on the FIRST field of a group to the id of the LAST field in that group.
              Everything between them (inclusive) will be boxed together when previewed.
            </p>
          </div>
          {(field.fromField === 'OPTION_FIELD' ||
            field.fromField === 'CONDITIONAL_SELECTED_FIELD') && (
            <div className="my-2 flex items-center">
              <AppRadioButton
                isChecked={field.isRow || false}
                onClick={(val: boolean) => {
                  onChange(idx, 'isRow', val);
                }}
              />
              <p
                className="text-sm ml-1 cursor-pointer"
                onClick={() => {
                  onChange(idx, 'isRow', !field.isRow);
                }}>
                Are options in a row or column
              </p>
            </div>
          )}
          {field.fromField === 'CONDITIONAL_SELECTED_FIELD' && (
            <div className="my-2 flex flex-col">
              <AppInputField
                label="Option List"
                value={conditionalOptions[field.id] ?? ''}
                onTextChange={(text) => {
                  setConditionalOptions((prev) => ({ ...prev, [field.id]: text }));
                  onChange(idx, 'optionList', text);
                }}
              />
              <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                Add option list separed by comma
              </p>
            </div>
          )}
          {field.fromField === 'CONDITIONAL_SELECTED_FIELD' && conditionalOptions[field.id] && (
            <div className="my-2 flex items-center w-full">
              <ConditionalSelectedOptionFields
                optionList={conditionalOptions[field.id]}
                ref={(el) => {
                  conditionalRefsMap.current[field.id] = el;
                }}
                onChangeConditionalField={handleChangeConditionalField}
                fieldId={field.id}
                valueConditionalFields={selectedConditionalOptionFields}
                filteredStardardFieldIds={standardFieldIds.filter(
                  (id) => !currentStepFieldIds.includes(id)
                )}
                currentStepFieldIds={currentStepFieldIds}
              />
            </div>
          )}
          {(field.fromField === 'CONDITIONAL_FORM' ||
            field.fromField === 'DYNAMIC_FORM_DOCUMENT') && (
            <div className="my-2 flex items-center flex-col">
              <AppInputField
                inputType="textarea"
                label="Form Template"
                value={field.formTemplate || ''}
                onTextChange={(text) => {
                  onChange(idx, 'formTemplate', text);
                }}
              />
            </div>
          )}
          {field.fromField === 'DYNAMIC_FORM_DOCUMENT' && (
            <FormDocumentFields
              // optionList={conditionalOption}
              ref={dynamicFormRef}
              onChangeConditionalField={handleChangeFormDocumentField}
              fieldId={field.id}
              valueConditionalFields={selectedConditionalOptionFields}
              filteredStardardFieldIds={standardFieldIds.filter(
                (id) => !currentStepFieldIds.includes(id)
              )}
              currentStepFieldIds={currentStepFieldIds}
            />
          )}

          {/* Conditional visibility — show this field only when another field has a specific value */}
          <div className="my-2 border border-gray-100 p-2 rounded">
            <p className="text-xs font-medium text-gray-500 mb-1">Show this field only if…</p>
            <div className="flex flex-col gap-2">
              <div>
                <AppInputField
                  label="Source Field ID"
                  placeholder="e.g. has-business-license"
                  value={field.conditionalVisibility?.sourceFieldId ?? ''}
                  onTextChange={(text) => {
                    const current = field.conditionalVisibility ?? {
                      sourceFieldId: '',
                      sourceOptionValues: [],
                    };
                    onChange(
                      idx,
                      'conditionalVisibility',
                      text ? { ...current, sourceFieldId: text } : null
                    );
                  }}
                />
              </div>
              <div>
                <AppInputField
                  label="Option Values (comma-separated)"
                  placeholder="e.g. Yes, Maybe"
                  value={
                    condVisRawText[idx] ??
                    (field.conditionalVisibility?.sourceOptionValues ?? []).join(', ')
                  }
                  onTextChange={(text) => {
                    setCondVisRawText((prev) => ({ ...prev, [idx]: text }));
                  }}
                  onBlur={() => {
                    const raw = condVisRawText[idx] ?? '';
                    const current = field.conditionalVisibility ?? {
                      sourceFieldId: '',
                      sourceOptionValues: [],
                    };
                    onChange(idx, 'conditionalVisibility', {
                      ...current,
                      sourceOptionValues: raw
                        .split(',')
                        .map((s: string) => s.trim())
                        .filter(Boolean),
                    });
                  }}
                />
              </div>
            </div>
            {field.conditionalVisibility?.sourceFieldId &&
              (field.conditionalVisibility.sourceOptionValues ?? []).length > 0 && (
                <p className="text-xs mt-1 PRIMARY_300-CLR">
                  This field shows only when &quot;{field.conditionalVisibility.sourceFieldId}&quot;
                  is one of: &quot;
                  {field.conditionalVisibility.sourceOptionValues.join('", "')}&quot;
                </p>
              )}
          </div>

          {/* Conditional required — e.g. Personal Guarantor only required for LLC (Single-member) */}
          <div className="my-2 border border-gray-100 p-2 rounded">
            <p className="text-xs font-medium text-gray-500 mb-1">
              Required only if… (otherwise optional, still shown)
            </p>
            <div className="flex flex-col gap-2">
              <AppInputField
                label="Source Field ID"
                placeholder="e.g. company-type-options"
                value={field.conditionalRequired?.sourceFieldId ?? ''}
                onTextChange={(text) => {
                  const current = field.conditionalRequired ?? {
                    sourceFieldId: '',
                    sourceOptionValues: [],
                  };
                  onChange(
                    idx,
                    'conditionalRequired',
                    text ? { ...current, sourceFieldId: text } : null
                  );
                }}
              />
              <AppInputField
                label="Option Values (comma-separated)"
                placeholder="e.g. LLC (Single-member)"
                value={
                  condReqRawText[idx] ??
                  (field.conditionalRequired?.sourceOptionValues ?? []).join(', ')
                }
                onTextChange={(text) => {
                  setCondReqRawText((prev) => ({ ...prev, [idx]: text }));
                }}
                onBlur={() => {
                  const raw = condReqRawText[idx] ?? '';
                  const current = field.conditionalRequired ?? {
                    sourceFieldId: '',
                    sourceOptionValues: [],
                  };
                  onChange(idx, 'conditionalRequired', {
                    ...current,
                    sourceOptionValues: raw
                      .split(',')
                      .map((value: string) => value.trim())
                      .filter(Boolean),
                  });
                }}
              />
            </div>
            {field.conditionalRequired?.sourceFieldId &&
              (field.conditionalRequired.sourceOptionValues ?? []).length > 0 && (
                <p className="text-xs mt-1 PRIMARY_300-CLR">
                  Required only when &quot;{field.conditionalRequired.sourceFieldId}&quot; is one
                  of: &quot;{field.conditionalRequired.sourceOptionValues.join('", "')}&quot;
                </p>
              )}
          </div>
        </div>
      );
    });
  };

  const renderPreview = (fields: Field[]) => {
    return fields.map((field, idx) => {
      let fieldComponent = null;

      switch (field.fromField) {
        case 'TEXT':
        case 'NUMBER':
        case 'CURRENCY':
        case 'TEXT_AREA':
          fieldComponent = (
            <AppInputField
              label={field.name}
              labelSubtext={field.labelSubtext ?? ''}
              inputType={
                field.fromField === 'TEXT_AREA'
                  ? 'textarea'
                  : field.fromField === 'NUMBER' || field.fromField === 'CURRENCY'
                  ? 'number'
                  : 'text'
              }
              value=""
              isRequired={field.isRequired}
              suffix={field.suffix}
              prefix={field.prefix}
              onTextChange={(text) => {
                console.log(`Field ${field.name} changed to:`, text);
              }}
            />
          );
          break;

        case 'DATE':
          fieldComponent = (
            <AppDatePicker
              label={field.name}
              labelSubtext={field.labelSubtext ?? ''}
              selectedDate={new Date()}
              isRequired={field.isRequired}
              showMonthYearPicker={field.datePickerFormat === 'MONTH_YEAR'}
              dateFormat={field.datePickerFormat === 'MONTH_YEAR' ? 'MM/yyyy' : 'MM/dd/yyyy'}
              onDateSelected={(date) => {
                console.log(`Field ${field.name} selected date:`, date);
              }}
            />
          );
          break;

        case 'CHECK_BOX':
          fieldComponent = (
            <div>
              <div
                className={`my-1 flex items-center w-full ${
                  field.alignment === 'RIGHT'
                    ? 'justify-end'
                    : field.alignment === 'CENTER'
                    ? 'justify-center'
                    : 'justify-start'
                }`}>
                <DillCheckbox isChecked={false} />
                <div className="ml-2 text-sm">{field.name}</div>
              </div>
              {field.labelSubtext &&
                (isHTML(field.labelSubtext) ? (
                  <p
                    className={`text-2xs ml-2 GREY_500-CLR w-full ${
                      field.alignment === 'RIGHT'
                        ? 'text-end'
                        : field.alignment === 'CENTER'
                        ? 'text-center'
                        : 'text-start'
                    }`}
                    dangerouslySetInnerHTML={{ __html: field.labelSubtext }}
                  />
                ) : (
                  <p
                    className={`text-2xs ml-2 GREY_500-CLR w-full ${
                      field.alignment === 'RIGHT'
                        ? 'text-end'
                        : field.alignment === 'CENTER'
                        ? 'text-center'
                        : 'text-start'
                    }`}>
                    {field.labelSubtext}
                  </p>
                ))}
            </div>
          );
          break;

        case 'FILE':
          fieldComponent = (
            <div className="my-1 flex items-center">
              <UploadFileInputs
                isRequired={field.isRequired}
                label={field.name}
                labelSubtext={field.labelSubtext ?? ''}
                onSelectFile={() => {}}
              />
            </div>
          );
          break;

        case 'OPTION_FIELD':
          fieldComponent = (
            <div className="my-1 flex items-center">
              <RadioInputs
                label={field.name}
                onChange={() => {}}
                id={field.name}
                options={resolveFieldOptions(field, branches, salesReps)}
                optionDescriptions={
                  field?.optionDescriptions
                    ? field.optionDescriptions.split(';;').map((option) => option.trim())
                    : undefined
                }
                isRequired={field.isRequired}
                selectedOption=""
                className={field.isRow ? 'gap-2 flex items-center' : ''}
              />
            </div>
          );
          break;

        case 'DOCUMENT_FIELD':
          fieldComponent = (
            <div className="my-1 flex items-center w-full">
              <DocumentField
                text={field.documentText || ''}
                className={`w-full ${field.fontWeight} text-[${field.fontSize ?? '12'}]px ${
                  field.alignment === 'RIGHT'
                    ? 'text-right'
                    : field.alignment === 'CENTER'
                    ? 'text-center'
                    : 'text-left'
                }`}
              />
            </div>
          );
          break;

        case 'INVITE_USER':
          fieldComponent = (
            <InviteSignature
              data={
                field?.whoNeedsToSign
                  ? field?.whoNeedsToSign.split(',').map((option) => {
                      return {
                        title: option.trim(),
                        email: '',
                        step: 0,
                        signatureGroupName: field.name,
                        placeHolder: option.trim(),
                        type: field.type ?? 'AUTHORIZED_AGENT',
                        minimumCreditAmount: field.minimumCreditAmount,
                        fields: field.invitedSignaturesFields || [],
                        isRequired: field.isRequired,
                        minimumNumberOfSignatures: field.minimumNumberOfSignatures,
                      };
                    })
                  : ([] as {
                      title: string;
                      email: string;
                      step: number;
                      signatureGroupName: string;
                      placeHolder: string;
                      type:
                        | 'AUTHORIZED_AGENT'
                        | 'GUARANTOR'
                        | 'OTHER'
                        | 'PERSONAL'
                        | 'CORPORATE_GUARANTOR';
                      minimumCreditAmount: number;
                      fields: DynamicInvitedSignaturesFields[];
                      isRequired: boolean;
                      minimumNumberOfSignatures?: number;
                    }[])
              }
              onChange={() => {}}
              value={
                [] as {
                  title: string;
                  email: string;
                  step: number;
                  signatureGroupName: string;
                  placeHolder: string;
                  type:
                    | 'AUTHORIZED_AGENT'
                    | 'GUARANTOR'
                    | 'OTHER'
                    | 'PERSONAL'
                    | 'CORPORATE_GUARANTOR';
                  minimumCreditAmount: number;
                  fields: DynamicInvitedSignaturesFields[];
                }[]
              }
            />
          );
          break;

        case 'CONDITIONAL_OPTION':
          fieldComponent = (
            <ConditionalOption
              optionType={field.optionType || 'text'}
              id={field.id}
              onChange={() => {}}
              isRequired={field.isRequired}
              label={field.name}
              selectedValue={{ value: '', secondaryValue: '' }}
              secondaryText={field.secondaryText}
              defaultOption={'Yes'}
            />
          );
          break;

        case 'BANK_REFERENCE':
          fieldComponent = (
            <AppBankReference
              onSubmit={() => {}}
              bankDetails={null}
              bankError={null}
              bankReferenceRequiredFields={bankReferenceRequiredFields}
              supplierName={currentSupplier?.name ?? 'Supplier'}
              checkRequired={field.isRequired}
              onlyShowPlaidOption={field.onlyShowPlaidOption}
            />
          );
          break;

        case 'TRADE_REFERENCE':
          fieldComponent = (
            <div className="flex flex-col gap-2 w-full">
              <AppTradeReferenceUpload
                value={null}
                onChange={() => {}}
                onAcceptExtractedReferences={() => {}}
                isRequired={field.isRequired}
                supplierId={currentSupplier?.id ?? ''}
                maxReferenceSlots={3}
              />
              {conditionalOptionFields?.[field.id] && (
                <DynamicForm
                  selectedValueOption={''}
                  selectedFieldValue={[]}
                  creditApplicationOptionData={conditionalOptionFields?.[field.id] || null}
                  name={field.name}
                  onChangeData={() => {}}
                />
              )}
            </div>
          );
          break;

        case 'CONDITIONAL_SELECTED_FIELD':
          fieldComponent = (
            <PreviewConditionalSelectedField
              creditApplicationOptionData={conditionalOptionFields?.[field.id] || null}
              name={field.name}
              isRequired={field.isRequired}
              isRow={field.isRow}
              optionList={field.optionList}
            />
          );
          break;

        case 'STATE_DROPDOWN':
          fieldComponent = (
            <StateDropDown
              isRequired={field.isRequired}
              label={field.name}
              value={''}
              errorText={''}
              onTextChange={() => {}}
            />
          );
          break;

        case 'CHECKBOX_LIST':
          fieldComponent = (
            <CheckboxList
              isRequired={field.isRequired}
              label={field.name}
              options={resolveFieldOptions(field, branches, salesReps)}
              value={''}
              errorText={''}
              onTextChange={() => {}}
            />
          );
          break;

        case 'DROPDOWN':
          fieldComponent = (
            <OptionsDropDown
              isRequired={field.isRequired}
              label={field.name}
              options={resolveDropdownFieldOptions(field, branches, salesReps)}
              value={''}
              errorText={''}
              onTextChange={() => {}}
            />
          );
          break;

        case 'CONDITIONAL_FORM':
          fieldComponent = (
            <ConditionalForm
              optionType={field.optionType || 'text'}
              id={field.id}
              onChange={() => {}}
              isRequired={field.isRequired}
              label={field.name}
              selectedValue={{ value: '', secondaryValue: '' }}
              secondaryText={field.secondaryText}
              defaultOption={'Yes'}
              formTemplate={field.formTemplate}
            />
          );
          break;

        case 'DYNAMIC_FORM_DOCUMENT':
          fieldComponent = (
            <DynamicForm
              selectedValueOption={''}
              selectedFieldValue={[]}
              creditApplicationOptionData={conditionalOptionFields?.[field.id] || null}
              name={field.name}
              formTemplate={field.formTemplate}
              onChangeData={() => {}}
            />
          );
          break;

        case 'AUTOFILL_CHECKBOX':
          fieldComponent = (
            <div>
              <div className="my-1 flex items-center w-full">
                <DillCheckbox isChecked={false} onChange={() => {}} />
                <div className="ml-2 text-sm">{field.name}</div>
              </div>
              {field.labelSubtext &&
                (isHTML(field.labelSubtext) ? (
                  <p
                    className="text-2xs ml-2 GREY_500-CLR w-full text-start"
                    dangerouslySetInnerHTML={{ __html: field.labelSubtext }}
                  />
                ) : (
                  <p className="text-2xs ml-2 GREY_500-CLR w-full text-start">
                    {field.labelSubtext}
                  </p>
                ))}
            </div>
          );
          break;

        default:
          fieldComponent = (
            <div className="text-sm text-gray-400">{`${field.fromField} to be added`}</div>
          );
          break;
      }

      return (
        <div key={idx} className="w-full">
          <div className="px-2 py-2">{fieldComponent}</div>
        </div>
      );
    });
  };

  const removeGroup = (groupIndex: number) => {
    setGroupedFields((prev) => {
      const updated = [...prev];
      updated.splice(groupIndex, 1);
      return updated;
    });
  };

  return (
    <Modal open={open}>
      <div
        style={{ maxHeight: '90vh' }}
        className="flex min-h-80 w-6/12 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg flex-col overflow-scroll">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white py-2 px-4 GREY_300-BORDER border-b">
          <X
            className="absolute right-2 top-2 cursor-pointer"
            color={COLORS.GREY_500}
            onClick={handleCloseModal}
          />
          <h3 className="text-lg font-bold">Add Step Field</h3>
        </div>

        {/* Body */}
        <div className="p-4">
          <AppDropDown
            label="Type"
            isRequired
            value={groupType}
            errorText={errorFields.includes('groupType') ? 'Please select' : ''}
            items={CREDIT_FIELD_TYPES.map((type) => ({
              label: type.replaceAll('_', ' '),
              value: type,
            }))}
            onSelectChange={(item) => {
              if (item) {
                setGroupType(item.value);
                setIsFieldArray(false);
                setSendEmail(false);
              }
            }}
          />

          {groupType === 'GROUPED' && (
            <>
              <AppInputField
                isRequired
                label="Group Name"
                placeholder="Enter group name"
                value={groupName}
                errorText={errorFields.includes('groupName') ? 'Group name is required' : ''}
                onTextChange={(text) => setGroupName(text)}
              />
              {groupedFields.map((field, i) => {
                const columns = getColumnsCount(field.columnType);
                const fieldsArray = Array(columns)
                  .fill(null)
                  .map(
                    (_, idx) =>
                      field.fields[idx] || {
                        name: '',
                        id: '',
                        fromField: '',
                        isRequired: false,
                        prefix: '',
                        suffix: '',
                      }
                  );

                return (
                  <div key={field.id} className="w-full rounded GREY_300-BORDER border p-2 my-2">
                    <AppDropDown
                      label="Number of Columns"
                      isRequired
                      value={field.columnType}
                      items={COLUMN_TYPES.map((c) => ({ label: c.replaceAll('_', ' '), value: c }))}
                      onSelectChange={(item) => {
                        if (!item) return;

                        setGroupedFields((prev) => {
                          const updated = [...prev];
                          const group = updated[i];
                          const columnCount = getColumnsCount(item.value);

                          const newFields = Array(columnCount)
                            .fill(null)
                            .map((_, index) => {
                              return (
                                group.fields[index] || {
                                  name: '',
                                  id: '',
                                  fromField: 'TEXT' as const,
                                  isRequired: false,
                                  prefix: '',
                                  suffix: '',
                                }
                              );
                            });

                          group.columnType = item.value;
                          group.fields = newFields;
                          updated[i] = group;
                          return updated;
                        });
                      }}
                    />
                    <div className={`grid ${getGridColsClass(columns)} gap-2 mt-2`}>
                      {renderFieldInputs(fieldsArray, i, (fieldIdx, key, val) => {
                        setGroupedFields((prev) => {
                          const updated = [...prev];
                          const targetGroup = { ...updated[i] };
                          const targetFields = [...(targetGroup.fields || [])];

                          const existing = targetFields[fieldIdx] || {
                            name: '',
                            id: '',
                            fromField: '',
                            isRequired: false,
                            prefix: '',
                            suffix: '',
                          };

                          const newField = { ...existing, [key]: val };

                          targetFields[fieldIdx] = newField;
                          targetGroup.fields = targetFields;
                          updated[i] = targetGroup;

                          return updated;
                        });
                      })}
                    </div>
                    {groupedFields.length > 1 && (
                      <div
                        className="w-full cursor-pointer  flex justify-end my-2 text-red-500 items-center"
                        onClick={() => removeGroup(i)}>
                        <div className="text-sm mr-1">Remove</div>
                        <Trash color={COLORS.ERROR_400} size={16} />
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="my-2">
                <AppButton
                  text="Add Field"
                  onClick={() =>
                    setGroupedFields((prev) => [
                      ...prev,
                      {
                        id: `field-${prev.length + 1}`,
                        index: prev.length + 1,
                        columnType: 'ONE_COLUMN',
                        stepType: 'GROUPED',
                        fields: [],
                        groupedPosition: 0,
                      },
                    ])
                  }
                  type="PRIMARY"
                  buttonStyles={{ width: '100px', height: '35px' }}
                />
              </div>
              <AppInputField
                label="Minimum Number of Groups"
                value={minimunNumberOfGroups.toString()}
                onTextChange={(text) => setMinimunNumberOfGroups(Number(text))}
                inputType="number"
                isRequired
                placeholder="Enter minimum number of groups"
                onBlur={() => {
                  if (minimunNumberOfGroups < 1) {
                    setMinimunNumberOfGroups(1);
                  }
                }}
                errorText={
                  errorFields.includes('minimunNumberOfGroups')
                    ? 'Minimum number of groups is required'
                    : ''
                }
              />
              <div className="my-3 flex items-center">
                <AppRadioButton
                  isChecked={isFieldArray}
                  onClick={(val: boolean) => setIsFieldArray(val)}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() => setIsFieldArray(!isFieldArray)}>
                  Has multiple fields
                </p>
              </div>
              {isFieldArray && (
                <AppInputField
                  label="More Button Name"
                  placeholder="Enter button name for adding more"
                  value={moreButtonName}
                  onTextChange={(text) => setMoreButtonName(text)}
                />
              )}
              <div className="my-3 flex items-center">
                <AppRadioButton
                  isChecked={sendEmail}
                  onClick={(val: boolean) => setSendEmail(val)}
                />
                <p className="text-sm ml-1 cursor-pointer" onClick={() => setSendEmail(!sendEmail)}>
                  Send Email and Follow up
                </p>
              </div>
              <div className="my-3 flex items-center">
                <AppRadioButton
                  isChecked={hasTradeReferenceUpload}
                  onClick={(val: boolean) => setHasTradeReferenceUpload(val)}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() => setHasTradeReferenceUpload(!hasTradeReferenceUpload)}>
                  Include trade reference OCR upload
                </p>
              </div>
            </>
          )}

          {groupType === 'NOT_GROUPED' && (
            <>
              <AppDropDown
                label="Number of Columns"
                isRequired
                value={fieldColumn}
                errorText={errorFields.includes('fieldColumn') ? 'Please select' : ''}
                items={COLUMN_TYPES.map((type) => ({
                  label: type.replaceAll('_', ' '),
                  value: type,
                }))}
                onSelectChange={(item) => {
                  if (!item) return;
                  setFieldColumn(item.value);

                  const columnCount = getColumnsCount(item.value);
                  const newFields = Array(columnCount)
                    .fill(null)
                    .map(() => ({
                      name: '',
                      id: '',
                      fromField: 'TEXT' as const,
                      isRequired: false,
                      prefix: '',
                      suffix: '',
                    }));

                  setUnGroupedFields(newFields);
                }}
              />
              <div className={`grid ${getGridColsClass(getColumnsCount(fieldColumn))} gap-2 mt-2`}>
                {renderFieldInputs(
                  Array(getColumnsCount(fieldColumn))
                    .fill(null)
                    .map(
                      (_, i) =>
                        unGroupedFields[i] || {
                          name: '',
                          id: '',
                          fromField: 'TEXT',
                          isRequired: false,
                          prefix: '',
                          suffix: '',
                        }
                    ),
                  0,
                  (idx, key, val) => {
                    setUnGroupedFields((prev) => {
                      const updated = [...prev];
                      const existing = updated[idx] || {
                        name: '',
                        id: '',
                        fromField: '',
                        isRequired: false,
                        prefix: '',
                        suffix: '',
                      };

                      const newField = { ...existing, [key]: val };

                      updated[idx] = newField;
                      return updated;
                    });
                  }
                )}
              </div>
            </>
          )}
        </div>
        <div className="my-2 p-4">
          <p className="text-sm font-bold mb-2">Preview</p>
          {groupType === 'GROUPED' && (
            <div className="border border-blue-500 my-2">
              <p className="text-sm font-bold ml-2">{groupName}</p>
              {sendEmail && (
                <div className="flex flex-row-reverse">
                  <AppButton
                    text="Send Email"
                    onClick={() => {}}
                    type="SECONDARY"
                    icon={<Mail className={COLORS.PRIMARY_500} size={16} />}
                    buttonStyles={{
                      height: '35px',
                      alignItems: 'center',
                      display: 'flex',
                    }}
                  />
                </div>
              )}
              {groupedFields.map((field, i) => {
                const columns = getColumnsCount(field.columnType);
                const fieldsArray = Array(columns)
                  .fill(null)
                  .map(
                    (_, idx) =>
                      field.fields[idx] || {
                        name: '',
                        id: '',
                        fromField: 'TEXT',
                        isRequired: false,
                        prefix: '',
                        suffix: '',
                      }
                  );

                return (
                  <div key={field.id} className="w-full">
                    <div className="flex gap-2 ">{renderPreview(fieldsArray)}</div>
                  </div>
                );
              })}
              {isFieldArray && (
                <div className="w-full flex mb-2 justify-end">
                  <AppButton
                    text={`Add ${groupName}`}
                    onClick={() => {}}
                    icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                    buttonStyles={{
                      height: '30px',
                      borderRadius: '5px',
                    }}
                  />
                </div>
              )}
            </div>
          )}
          {groupType === 'NOT_GROUPED' && (
            <div className="flex gap-2 mt-2 border border-blue-500 ">
              {renderPreview(unGroupedFields)}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="GREY_300-BORDER border-t">
          {(errorFields.length > 0 ||
            conditionalOptionError !== '' ||
            dynamicFormFieldsError !== '') && (
            <p className="text-xs text-red-600 text-center">There are some errors</p>
          )}
          <div className="flex justify-between p-4 w-full">
            <AppButton text="Close" onClick={handleCloseModal} type="TERTIARY" />
            <AppButton text="Submit" onClick={handleSubmit} />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreditApplicationFieldsModal;
